import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { selectFetchStatusEndpointsInterval } from "~/features/andon/andon.slice";
import { StoreState } from "~/redux/reducers";
import { andonApi, GetAndonWorkstationsArg } from "~/redux/warehouse/andon";
import { AndonWorkstation } from "~/types/api";

export type UseAndonWorkstationsByFcIdsOptions = {
  poll?: boolean;
};

type AndonWorkstationsQueryResult = ReturnType<
  ReturnType<typeof andonApi.endpoints.getAndonWorkstations.select>
>;

type SelectedAndonWorkstations = {
  workstations: AndonWorkstation[] | undefined;
  isError: boolean;
};

const selectAndonWorkstationsByArgs = new Map<
  string,
  (state: StoreState) => SelectedAndonWorkstations
>();

function uniqueSortedFcIds(fcIds: string[]): string[] {
  return [...new Set(fcIds.filter(Boolean))].sort();
}

function queryArgsFromFcKey(fcKey: string): GetAndonWorkstationsArg[] {
  if (!fcKey) return [undefined];
  return fcKey
    .split(",")
    .map((fulfillmentCenterId) => ({ fulfillmentCenterId }));
}

function getSelectAndonWorkstations(queryArgs: GetAndonWorkstationsArg[]) {
  const cacheKey = JSON.stringify(queryArgs);
  const cached = selectAndonWorkstationsByArgs.get(cacheKey);
  if (cached) {
    return cached;
  }

  const selector = createSelector(
    queryArgs.map((arg) => andonApi.endpoints.getAndonWorkstations.select(arg)),
    (...results: AndonWorkstationsQueryResult[]): SelectedAndonWorkstations => {
      const allSettled = results.every(
        (result) => result.data != null || result.isError
      );
      return {
        workstations: allSettled
          ? results.flatMap((result) => result.data?.workstations ?? [])
          : undefined,
        isError: allSettled && results.some((result) => result.isError)
      };
    }
  );

  selectAndonWorkstationsByArgs.set(cacheKey, selector);
  return selector;
}

// Empty list = site FC (default cache entry). Non-empty = one cache entry per FC.
export function useAndonWorkstationsByFcIds(
  fcIds: string[],
  options: UseAndonWorkstationsByFcIdsOptions = {}
): {
  workstations: AndonWorkstation[] | undefined;
  isError: boolean;
} {
  const poll = options.poll ?? true;
  const dispatch = useAppDispatch();
  const pollingInterval = useAppSelector(selectFetchStatusEndpointsInterval);
  const fcKey = uniqueSortedFcIds(fcIds).join(",");
  const previousSnapshotRef = useRef<{
    fcKey: string;
    workstations: AndonWorkstation[];
  }>(undefined);
  const subscriptionsRef = useRef<
    Array<{
      unsubscribe: () => void;
      updateSubscriptionOptions: (options: {
        pollingInterval?: number;
        refetchOnFocus?: boolean;
        skipPollingIfUnfocused?: boolean;
      }) => void;
    }>
  >([]);

  const queryArgs = useMemo(() => queryArgsFromFcKey(fcKey), [fcKey]);

  useEffect(() => {
    const subscriptions = queryArgs.map((arg) =>
      dispatch(
        andonApi.endpoints.getAndonWorkstations.initiate(arg, {
          subscriptionOptions: {
            pollingInterval:
              poll && pollingInterval > 0 ? pollingInterval : undefined,
            refetchOnFocus: poll,
            skipPollingIfUnfocused: true
          }
        })
      )
    );
    subscriptionsRef.current = subscriptions;

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
      subscriptionsRef.current = [];
    };
    // Subscribe when the FC set or poll flag changes. Interval updates go through
    // updateSubscriptionOptions so we do not tear down and refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, poll, queryArgs]);

  useEffect(() => {
    // Interval changes must update the live subscription, not resubscribe.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!poll) return;
    subscriptionsRef.current.forEach((subscription) => {
      subscription.updateSubscriptionOptions({
        pollingInterval: pollingInterval > 0 ? pollingInterval : undefined,
        refetchOnFocus: true,
        skipPollingIfUnfocused: true
      });
    });
  }, [poll, pollingInterval]);

  const selected = useAppSelector(getSelectAndonWorkstations(queryArgs));
  if (selected.workstations) {
    previousSnapshotRef.current = {
      fcKey,
      workstations: selected.workstations
    };
  }

  const previousWorkstations =
    previousSnapshotRef.current?.fcKey === fcKey
      ? previousSnapshotRef.current.workstations
      : undefined;

  return {
    workstations: selected.workstations ?? previousWorkstations,
    isError: selected.isError
  };
}
